import { Fragment } from 'preact';
import { useEffect, useState, useRef } from 'preact/hooks';
import { range, debounce } from '@/utils';
import api from '@/api';

import { useWcagKeyboardEvents } from '@/features/conversational';
import {
	SvgIcon,
	useAdvanced,
	useFormStore,
	useFormProps,
	useProperties,
	useConfigStore,
	useBlock
} from '@/features/form';

import Selectable from './Selectable';

export default function Rating({
	block,
	state,
	setValue,
	showScoreOnly,
	showErrors,
	disabled = false,
	role,
	elementIndex
}) {
	const { cacheDomain, domain } = useFormProps();
	const { currentConversationalFocusRef, actions, formType, lastKeyPressed, activePage } =
		useFormStore();
	const { text } = useConfigStore();
	const { conversational } = useProperties();
	const { lastPageAutoPost } = useAdvanced();
	const [hoverScore, setHoverScore] = useState(-1);
	const [emojis, setEmojis] = useState({
		angry: '',
		sad: '',
		neutral: '',
		happy: '',
		'extra-happy': ''
	});

	const currentPageBreak = useBlock({
		typeName: 'page_break',
		typeOf: activePage - 1
	});

	const withWcagKeyboardEvents =
		conversational ||
		(lastPageAutoPost && formType === 'embed') ||
		(currentPageBreak?.block?.properties?.autopost && formType === 'embed');

	useEffect(() => {
		if (conversational && elementIndex !== 0) {
			actions.handleConversationalFocus();
			actions.updateLastKeyPressed(null);
		}
	}, []);

	const { onKeyDown, onChange, onClick, pendingScore, updatePendingScore } =
		useWcagKeyboardEvents({
			onArrowKeyPressed: e => {
				const updateHelperText = debounce(
					() => actions.updateScreenReaderHelper(text?.wcag?.ratingHelperText ?? ''),
					1000
				);
				updateHelperText();
				actions.updateLastKeyPressed(e.key);
			},
			onTabSpaceEnter: () => {
				if (pendingScore) {
					setValue(pendingScore);
					updatePendingScore(null);
					setTimeout(() => {
						actions.updateScreenReaderHelper('');
					}, 100);
				}
			},
			onChangedValue: e => {
				updatePendingScore(e.target.value);
				actions.updateLastKeyPressed(null);
			},
			onChangedValueConfirm: e => {
				actions.handleConversationalFocus(true);
				setValue(pendingScore);
				updatePendingScore(null);
			},
			onClickValue: (e, isMouseClick) => {
				updatePendingScore(!isMouseClick ? e.target.value : null);
				setTimeout(() => {
					actions.updateScreenReaderHelper('');
				}, 100);
			}
		});

	const ratingRefs = useRef({});
	const wrapperRef = useRef(null);

	const { properties, typeName } = block;
	const selectedScore = (pendingScore ?? state.value) || -1;
	const emojiOrder = ['angry', 'sad', 'neutral', 'happy', 'extra-happy'];
	const barInner = ['--', '-', '0', '+', '++'];

	useEffect(() => {
		//get emojis
		if (block.properties.type === 'emoji') {
			api({ domain, cacheDomain })
				.getEmoji(block.id)
				.then(emojis => setEmojis({ ...emojis }));
		}
	}, []);

	const getStateClass = score => {
		return `${
			(hoverScore === -1 && !properties.reverseScore && score > selectedScore) ||
			(hoverScore === -1 && properties.reverseScore && score < selectedScore) ||
			(hoverScore !== -1 && !properties.reverseScore && score > hoverScore) ||
			(hoverScore !== -1 && properties.reverseScore && score < hoverScore)
				? 'ui-stars-star-default'
				: ''
		}
			${
				(hoverScore !== -1 && !properties.reverseScore && score <= hoverScore) ||
				(hoverScore !== -1 && properties.reverseScore && score >= hoverScore)
					? 'ui-stars-star-hover'
					: ''
			}
			${
				(hoverScore === -1 &&
					selectedScore !== -1 &&
					!properties.reverseScore &&
					score <= selectedScore) ||
				(hoverScore === -1 &&
					selectedScore !== -1 &&
					properties.reverseScore &&
					score >= selectedScore)
					? 'ui-stars-star-on'
					: ''
			}`;
	};

	//Get the rating scale
	const scale =
		typeName === 'nps'
			? 10
			: properties.type === 'bar' || typeName === 'ces' || properties.type === 'emoji'
			? 5
			: properties.scale;

	//Should the rating start at 0 or 1
	const start =
		properties.type === 'numeric' || properties.type === 'stars'
			? properties.includeZero
				? 0
				: 1
			: typeName === 'nps'
			? 0
			: 1;

	//Get the scores in the right order
	const list = properties.reverseScore
		? range(start, scale).reverse()
		: range(start, scale);

	//check for rating legends
	const legendLow = properties.legend ? properties.legend.legendLow : '';
	const legendHigh = properties.legend ? properties.legend.legendHigh : '';

	const emojiList = properties.reverseScore ? [...emojiOrder].reverse() : emojiOrder;

	const selectedOrHoverScore =
		hoverScore > -1 ? hoverScore : selectedScore > -1 ? selectedScore : -1;

	const selectedOrHoverClass =
		selectedOrHoverScore > -1 ? `score-${selectedOrHoverScore}` : '';

	const selectedOrHoverScoreLabel =
		selectedOrHoverScore > -1 ? properties.elements?.[selectedOrHoverScore]?.label : null;

	return (
		<div
			ref={currentConversationalFocusRef}
			class={`rating-group ${typeName}-group ${typeName === 'ces' ? typeName : ''} ${
				properties.type ? properties.type : ''
			} ${properties.type ? properties.type : ''}-group ${
				['emoji', 'stars'].indexOf(properties.type) > -1 && properties.showCaptions
					? 'show-labels'
					: ''
			} ${properties.reverseScore ? 'reversed' : ''} ${
				properties.legend && (properties.legend.legendLow || properties.legend.legendHigh)
					? 'has-legend'
					: ''
			} ${!properties.required ? 'with-dummyscore' : ''}`}
		>
			<div
				class={`${properties.type === 'stars' || typeName === 'ces' ? '' : 'choice-row'}`}
				ref={wrapperRef}
				role="radiogroup"
				{...(properties.required && { 'aria-required': true })}
				{...(conversational && {
					//for conversational, this div is not part of fieldset > legend, so we need aria-labelledby to connect title to radiogroup
					'aria-labelledby': `block-title-${typeName}-${block.id}`
				})}
			>
				{list.map((score, i) => {
					let scoreValue =
						['emoji', 'stars'].indexOf(properties.type) > -1 && properties.labelsAsValue
							? properties[properties.type === 'emoji' ? 'emoji' : 'elements'][score]
									.label
							: score;

					if (showScoreOnly && String(state.value) !== String(scoreValue)) {
						return null;
					}

					const scoreLabel =
						properties[properties.type === 'emoji' ? 'emoji' : 'elements'] &&
						properties[properties.type === 'emoji' ? 'emoji' : 'elements'][score]
							? properties[properties.type === 'emoji' ? 'emoji' : 'elements'][score]
									.label
							: score;

					let attr = {
						onChange: e => {
							if (withWcagKeyboardEvents && pendingScore) {
								onChange(e, { key: lastKeyPressed });
							} else {
								setValue(e.target.value);
								setHoverScore(-1);
							}
						},
						block,
						key: i,
						selectableIndex: i,
						value: scoreValue,
						checked: String(selectedScore) === String(scoreValue),
						ariaLabel: scoreLabel,
						...(properties.required &&
							showErrors &&
							state.showError && {
								ariaInvalid: true,
								'aria-describedby': `error-message-${block.id}`
							}),
						...(showScoreOnly && { tabIndex: -1 }),
						disabled,
						role,
						onKeyDown: e => {
							if (withWcagKeyboardEvents) {
								onKeyDown(e);
							}
						},
						onClick: e => {
							if (withWcagKeyboardEvents) {
								onClick(e);
							}
						}
					};

					if (properties.type === 'emoji') {
						attr.labelAtts = {
							class: 'emoji-' + emojiList[i],
							'aria-label': properties.emoji[score].label ?? score,
							'data-label': properties.emoji[score].label ?? score
						};
					}
					if (properties.type === 'stars' || typeName === 'ces') {
						attr.labelAtts = {
							class: 'ui-stars-star star_' + score,
							'aria-label': properties.elements[score].label ?? score
						};

						return (
							<Selectable
								{...attr}
								ref={ref => (ratingRefs.current[score] = ref)}
							>
								<span
									className={`ui-stars-star star_${score} ${getStateClass(score)}`}
									onMouseEnter={() => {
										if (state.hoverScore !== score) {
											setHoverScore(score);
										}
									}}
									onMouseLeave={() => {
										setHoverScore(-1);
									}}
								>
									{properties.type === 'stars' && (
										<SvgIcon
											icon="star"
											className="is-before"
											alt={scoreLabel}
										/>
									)}
								</span>
							</Selectable>
						);
					} else {
						let html =
							properties.type === 'emoji'
								? { dangerouslySetInnerHTML: { __html: emojis[emojiList[i]] || null } }
								: null;

						return (
							<Selectable {...attr}>
								<span {...html}>
									{
										//properties.type === 'emoji' ? this.getEmoji(score,i,emojiList[i]) : score
										properties.type !== 'emoji' && properties.type !== 'bar'
											? score
											: properties.type === 'bar'
											? barInner[i]
											: null
									}
								</span>
							</Selectable>
						);
					}
				})}
				{properties.showCaptions &&
					(properties.type === 'stars' || typeName === 'ces') && (
						<Caption
							wrapperRef={wrapperRef}
							positioner={
								hoverScore !== -1
									? ratingRefs.current[hoverScore]
									: ratingRefs.current[selectedScore]
							}
							className={`caption ${selectedOrHoverClass}`}
							id={`caption-${block.id}-${hoverScore !== -1 ? hoverScore : selectedScore}`}
						>
							{selectedOrHoverScoreLabel}
						</Caption>
					)}
			</div>
			{properties.legend && !showScoreOnly && (
				<Fragment>
					<span
						className={`rating-legend legend-low ${
							pendingScore > -1 ? 'wcag-legend' : ''
						}`}
					>
						{legendLow}
					</span>
					<span
						className={`rating-legend legend-high ${
							pendingScore > -1 ? 'wcag-legend' : ''
						}`}
					>
						{legendHigh}
					</span>
				</Fragment>
			)}
		</div>
	);
}

function Caption({ positioner, className, children, id, wrapperRef }) {
	const captionRef = useRef(null);
	const left =
		positioner?.offsetLeft !== undefined
			? positioner.offsetLeft + positioner.scrollWidth / 2
			: null;

	if (children === null) return null;

	return (
		<span
			ref={captionRef}
			id={id}
			role="tooltip"
			style={{ left, opacity: left === null ? 0 : 1 }}
			className={className}
		>
			{children}
		</span>
	);
}
